import React, { ReactNode, useContext, useEffect, useState } from "react";
import { DesignerProviderValue, PhoenixFormsDesignerContext } from ".";
import { MCard } from "components/Shared";
import { KeyValueOriginal, MCardField } from "components/Shared/MCard";
import { setObjectValue } from "components/Shared/Functions";
import { DATA_TYPE_BOOLEAN, DATA_TYPE_DROPDOWN2, DATA_TYPE_JSX } from "typeConstants/dataTypes";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider";
import IconButton from "components/Shared/IconButton";
import { showExpressionEditor } from "./CodeEditor/ExpressionEditor";
import { createSelectGroupEnumItemId, getIdFromQuestion, getRequiredFieldsForToggle, isQuestionSelectGroup, isRequiredCheckboxChecked } from "./helpers";
import { findQuestionDef } from "./DesignerQuestionDefinitions";
import FormEnumerationEditor, { EnumerationEditorField } from "./FormEnumerationEditor";
import { EnumerationItem } from "interfaces/Phoenix.FormsDesigner.Models";
import ItemSetEditor from "./ItemSetEditor";
import TextField from "@mui/material/TextField";
import { stopPropagationPreventDefaultNoFn } from "tools/clickEvents";

const PhoenixFormDesignerProps = () => {
  const [autoSetId, setAutoSetId] = useState<boolean>();
  
  const context = useContext<DesignerProviderValue>(PhoenixFormsDesignerContext);
  const dialog = useContext<DialogProviderValue>(DialogContext);
  const { selectedQuestion } = context;
  const control = selectedQuestion?.control;
  // we will auto update the id if the control has a data field, and the id has not been set yet

  useEffect(() => {
    if (control) setAutoSetId(control.dataField && !control.dataField.bindableId);
  }, [control]);

  let cards = [<div>Select a control...</div>];

  if (control) {

    const onPropertyEdit = (changeArray: KeyValueOriginal[]) => {
      changeArray.forEach(ca => {
        setObjectValue(control, ca.key, ca.value);

        if (ca.key === "dataField.bindableId") setAutoSetId(false); // if they manually change the bindable id, dont auto change it anymore
        if (ca.key === "label") {
          if (autoSetId) {
            control.dataField.bindableId = getIdFromQuestion(selectedQuestion, context.formDefinition);
          }
          if (isRequiredCheckboxChecked(control.requiredExpression)) {
            const next = getRequiredFieldsForToggle(control, true);
            control.requiredExpression = next.requiredExpression;
            control.requiredMessage = next.requiredMessage;
          }
        }

        if (ca.key === "requiredExpression") {
          const { requiredExpression, requiredMessage } = getRequiredFieldsForToggle(control, ca.value);
          control.requiredExpression = requiredExpression;
          control.requiredMessage = requiredMessage;
        }
      });

      context.refreshFormDef("control field edited");
    };

    const showExpressionEditorLocal = (label: string, key: string) => {
      showExpressionEditor(dialog, `Editing ${label} for ${control.label}`, control[key],
        (newExpression: string) =>
          onPropertyEdit([{ key: key, originalValue: control[key], value: newExpression }])
      );
    };

    var controlTypeText = findQuestionDef(selectedQuestion)?.name;

    let hideFromOptions = [
      { label: "None", value: "" },
      { label: "Edit", value: "mobile|web" },
      { label: "Print", value: "print" },
    ];

    let cmsOptions = context.cmsList.map(c => ({ label: c.uniqueKey, value: c.uniqueKey }));

    let propertiesCardFields: MCardField[] = [
      { label: "Question Type", value: controlTypeText, editable: false },
      { key: "label", label: "Label" },
      {
        key: "requiredExpression",
        label: "Required",
        dataType: DATA_TYPE_BOOLEAN,
        value: isRequiredCheckboxChecked(control.requiredExpression),
      },
      { key: "dataField.defaultValue", label: "Default Value" },
      { key: "hint", label: "Hint" },
      autoSetId ?
        { key: "dataField.bindableId", label: "Id", dataType: DATA_TYPE_JSX, value: <div className="mcard-field-with-button"><span>{selectedQuestion.control.dataField.bindableId}</span><IconButton icon="edit" onClick={() => setAutoSetId(false)} /></div> }
        :
        { key: "dataField.bindableId", label: "Id", tooltip: control.dataField?.bindableId && { title: control.dataField?.bindableId } },
      { key: "dataField.thirdPartyID", label: "3rd Party Id" },
      { key: "dataField.exportId", label: "Export Id" },
      { key: "hideFrom", label: "Hide From", dataType: DATA_TYPE_DROPDOWN2, dataTypeOptions: { options: hideFromOptions, nullable: false }, value: hideFromOptions.find(o => o.value === control.hideFrom) },
      { key: "dataField.xpath", label: "Xpath" },
      { key: "contentCode", label: "Content Code", dataType: DATA_TYPE_DROPDOWN2, dataTypeOptions: { options: cmsOptions }, value: cmsOptions.find(o => o.value === control.contentCode) },
    ];

    propertiesCardFields.forEach(f => {
      if (f.value === undefined && f.key) f.value = f.key.split(".").reduce((acc, val) => acc ? acc[val] : undefined, control);
    });

    const getExpressionMCardField = (label: string, key: string): MCardField => ({
      key: key,
      editable: false,
      label: label,
      value: (
        <div className="mcard-field-with-button expression-field">
          <TextField
            variant="standard"
            onChange={e => onPropertyEdit([{ key: key, originalValue: control[key], value: e.target.value }])}
            value={control[key]}
          />
          <IconButton icon="code" onClick={() => showExpressionEditorLocal(label, key)} />
        </div>
      ),
      dataType: DATA_TYPE_JSX,
    });
    
    let displayCardFields: MCardField[] = [
      getExpressionMCardField("Conditional Display", "relevantExpression"),
      getExpressionMCardField("QA Summary View Display", "summaryExpression"),
      getExpressionMCardField("Read Only", "readonlyExpression"),
      getExpressionMCardField("Calculation", "calculationExpression"),
      getExpressionMCardField("Validation", "constraintExpression"),
      getExpressionMCardField("Validation Message", "constraintMessage"),
      { key: "mergeWithAbove", label: "Merge with Above", value: control.mergeWithAbove, dataType: DATA_TYPE_BOOLEAN },
    ];

    
    const hideForType = (controlType: string, fieldKeys: string[]) => {
      if (control.type === controlType) {
        propertiesCardFields = propertiesCardFields.filter(f => fieldKeys.indexOf(f.key) === -1);
        displayCardFields = displayCardFields.filter(f => fieldKeys.indexOf(f.key) === -1);
      }
    };

    hideForType("Group", ["hint", "dataField.bindableId", "dataField.thirdPartyID", "dataField.exportId", "dataField.xpath", "requiredExpression", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("Photo", ["dataField.bindableId", "dataField.thirdPartyID", "dataField.xpath", "hint", "dataField.defaultValue", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("Repeat", ["hint", "requiredExpression", "dataField.bindableId", "dataField.defaultValue", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("SelectGroup", ["hint", "dataField.bindableId", "dataField.thirdPartyID", "dataField.exportId", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("Calculate", ["hint", "requiredExpression", "dataField.defaultValue", "readonlyExpression", "constraintExpression", "constraintMessage", "hideFrom", "contentCode"]);
    hideForType("Ruleset", ["hint", "requiredExpression", "dataField.bindableId", "dataField.thirdPartyID", "dataField.exportId", "dataField.xpath", "dataField.defaultValue", "relevantExpression", "summaryExpression", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("Recommendation", ["hint", "requiredExpression", "dataField.bindableId", "dataField.thirdPartyID", "dataField.exportId", "dataField.xpath", "dataField.defaultValue", "relevantExpression", "summaryExpression", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    hideForType("Label", ["dataField.bindableId", "dataField.thirdPartyID", "dataField.exportId", "dataField.xpath"]);
    hideForType("File", ["dataField.bindableId", "dataField.thirdPartyID", "dataField.xpath", "hint", "dataField.defaultValue", "readonlyExpression", "calculationExpression", "constraintExpression", "constraintMessage"]);
    
    let showEnumeration = !!selectedQuestion?.control?.dataField?.enumeration;
    const enumerationItems = control.dataField.enumeration?.items;
    if (control.type.startsWith("DynamicItemset") || control.type.startsWith("AdvancedDynamicItemset") || control.type === "Ruleset" || control.type === "Photo") showEnumeration = false;

    cards = [
      <MCard
        key={`props-${context.selectedQuestion.id}-${autoSetId}`}
        onlyEditable
        onEditChanged={onPropertyEdit}
        fields={propertiesCardFields}
        title="Properties"
      />,
      <MCard
        className="phoenix-designer-props-display"
        key={`display-${context.selectedQuestion.id}`}
        title="Display"
        onlyEditable
        onEditChanged={onPropertyEdit}
        fields={displayCardFields}
      />
    ];

    const defaultEnumFields = (item: EnumerationItem) => {
      if (isSelectGroup) {
        item.bindableId = createSelectGroupEnumItemId(selectedQuestion.control.dataField.bindableId, item.name);
      }
      if (!item.value) item.value = item.name;
    }

    if (control.type === "Photo") {
      const staticName = "autotag";
      const expressionName = "expressiontag";
      const conditionalName = "conditionaltag";

      const updatePhotoEnumeration = (name: string) => (items: Partial<EnumerationItem>[]) => {
        // static tags get stored at the top as one tag... dont ask me why
        if (name === staticName) items = [{ value: items.map(i => i.value).join("|") }];
        items.forEach(i => i.name = name);

        let newItems = [];

        if (name === staticName) newItems.push(...items);
        else newItems.push(...enumerationItems.filter(e => e.name === staticName));

        if (name === expressionName) newItems.push(...items);
        else newItems.push(...enumerationItems.filter(e => e.name === expressionName));

        if (name === conditionalName) newItems.push(...items);
        else newItems.push(...enumerationItems.filter(e => e.name === conditionalName));

        context.editFns.setQuestionListItems(selectedQuestion.id, newItems);
      };

      let staticTags = [];
      // this could be a simple one liner, but then the split will create an empty "" tag that you cant get rid of beacuse of "".split
      if (enumerationItems?.length > 0) {
        let staticString = enumerationItems.find(i => i.name === staticName)?.value;
        if (staticString?.length > 0) staticTags = staticString.split("|").map(s => ({ value: s }));
      }

      cards.push(<FormEnumerationEditor
        title="Static Tags"
        items={staticTags}
        fields={[
          { label: "", property: "value", required: true },
        ]}
        bulkEditFields={["value"]}
        displayItem={item => item.value}
        onChange={updatePhotoEnumeration(staticName)}
        preAddLogic={defaultEnumFields}
      />);

      let expressionTags = enumerationItems.filter(i => i.name === expressionName) ?? [];
      cards.push(<FormEnumerationEditor
        title="Expression Tags"
        items={expressionTags}
        fields={[
          { label: "", property: "value", required: true, isExpression: true },
        ]}
        bulkEditFields={["value"]}
        displayItem={item => item.value}
        onChange={updatePhotoEnumeration(expressionName)}
        preAddLogic={defaultEnumFields}
      />);

      let conditionalTags = enumerationItems.filter(i => i.name === conditionalName) ?? [];
      cards.push(<FormEnumerationEditor
        title="Conditional Tags"
        items={conditionalTags}
        fields={[
          { label: "Tag", property: "value", required: true },
          { label: "Condition", property: "relevantExpression", required: true, isExpression: true },
        ]}
        bulkEditFields={["value", "relevantExpression"]}
        displayItem={item => `${item.value} (${item.relevantExpression})`}
        onChange={updatePhotoEnumeration(conditionalName)}
        preAddLogic={defaultEnumFields}
      />);
    }

    if (control.type === "DynamicItemsetSelectOne") {
      cards.push(<ItemSetEditor controlEnumerationItems={control.dataField.enumeration.items} onChange={newEnum => context.editFns.setQuestionListItems(selectedQuestion.id, newEnum)} />);
    }

    let isSelectGroup = isQuestionSelectGroup(selectedQuestion);

    if (showEnumeration) {
      let fields: EnumerationEditorField<EnumerationItem>[] = [];
      let bulkEditItems = [];
      if (isSelectGroup) {
        bulkEditItems = ["name", "value", "xpath", "bindableId", "exportId", "relevantExpression", "selectedExpression"];
        fields = [
          { label: "Text", property: "name", required: true, blockCommas: true },
          { label: "Value (if different from text)", property: "value", blockCommas: true },
          { label: "XPath", property: "xpath" },
          { label: "Export ID", property: "exportId" },
          { label: "Bindable ID", property: "bindableId", hideOnAdd: true },
          { label: "Conditional Display", property: "relevantExpression", isExpression: true },
          { label: "Selected Condition", property: "selectedExpression", isExpression: true },
        ];
      } else {
        bulkEditItems = ["name", "value"];
        fields = [
          { label: "Text", property: "name", required: true, blockCommas: true },
          { label: "Value (if different from text)", property: "value", blockCommas: true },
        ];
      }

      cards.push(<FormEnumerationEditor
        title="List Items"
        items={enumerationItems}
        fields={fields}
        bulkEditFields={bulkEditItems}
        onChange={newItems => context.editFns.setQuestionListItems(selectedQuestion.id, newItems)}
        displayItem={item => {
          let display: ReactNode[] = [item.name];
          if (item.name !== item.value) display[0] = `${display[0]} (${item.value})`;

          if (item.bindableId) display.push(<br />, `#${item.bindableId}`);
          if (item.xpath) display.push(<br />, `XPATH: ${item.xpath}`);
          if (item.exportId) display.push(<br />, `EXPORT-ID: ${item.exportId}`);
          if (item.relevantExpression) display.push(<br />, `CONDITIONAL: ${item.relevantExpression}`);
          if (item.selectedExpression) display.push(<br />, `SELECTED: ${item.selectedExpression}`);
          
          return display;
        }}
        preAddLogic={defaultEnumFields}
      />);
    }

    if (control.type.startsWith("AdvancedDynamicItemset")) {
      const updateItemset = (changeArray: KeyValueOriginal[]) => {
        let item = selectedQuestion.control.dataField.enumeration.items[0];
        changeArray.forEach(kv => item[kv.key] = kv.value);
        context.editFns.setQuestionListItems(selectedQuestion.id, [item]);
      };

      cards.push(<MCard
        key={`itemset-${context.selectedQuestion.id}-${autoSetId}`}
        title="Itemset"
        onlyEditable
        fields={[
          { key: "xpath", label: "Itemset Path", value: selectedQuestion.control.dataField?.enumeration?.items?.[0]?.xpath },
          { key: "name", label: "Label Path", value: selectedQuestion.control.dataField?.enumeration?.items?.[0]?.name },
          { key: "value", label: "Value Path", value: selectedQuestion.control.dataField?.enumeration?.items?.[0]?.value },
        ]}
        onEditChanged={updateItemset}
      />
      );
    }
  }

  return (
    <div className="designer-props">
      {cards}
    </div>
  );
};

export default PhoenixFormDesignerProps;
